import React, { useRef, useEffect, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import ScrollContainer from "react-indiana-drag-scroll"
import axios from "axios"
import { useScrollPosition } from "@n8tb1t/use-scroll-position"
import decodeShopifyProductId from "@utils/decodeShopifyProductId"
import toBase64String from "@utils/toBase64String"
import Arrow from "@assets/icons/arrow.inline.svg"
import { ProductCardV2 } from "@components/main/product-card-v2/ProductCardV2"

import * as s from "./ShopifyGallery.module.scss"

export const ShopifyGallery = ({ data, className }) => {
  const { shopifyData } = useStaticQuery(graphql`
    query ShopifyGalleryQuery {
      shopifyData: allShopifyProduct(filter: { vendor: { eq: "Live" } }) {
        nodes {
          handle
          tags
          metafields {
            key
            value
          }
          variants {
            availableForSale
            compareAtPrice
            price
            selectedOptions {
              name
              value
            }
            image {
              originalSrc
            }
            product {
              featuredImage {
                originalSrc
              }
            }
          }
          media {
            ... on ShopifyMediaImage {
              image {
                originalSrc
              }
            }
          }
          options {
            values
            name
          }
          description
          title
          priceRangeV2 {
            maxVariantPrice {
              amount
            }
            minVariantPrice {
              amount
            }
          }
          shopifyId
        }
      }
    }
  `)

  if (!shopifyData) {
    return <></>
  }

  const fetchData = async productId => {
    const shopifyRes = await axios.post(`/.netlify/functions/recommended`, {
      productId,
      locale: process.env.GATSBY_LOCALE || "uk",
    })
    return shopifyRes.data
  }  

  useEffect(() => {
    const productId = shopifyData.nodes[0].shopifyId
    const variantId = productId.includes("gid://shopify/")
      ? toBase64String(productId)
      : productId
    const currentProduct = decodeShopifyProductId(variantId)

    const recommendedProducts = async currentProduct => {
      const response = await fetchData(currentProduct)
      return response
    }

    recommendedProducts(currentProduct)
  }, [])

  const sort_by = data.sortBy
  const _scroller = useRef(null)
  const [isScrolling, setIsScrolling] = useState(null)
  const requestRef = useRef(null)
  const shopItems = shopifyData.nodes

  const handleKeyDown = event => {
    if (!_scroller.current) return

    if (event.keyCode === 37) {
      setIsScrolling(-1)
    } else if (event.keyCode === 39) {
      setIsScrolling(1)
    }
  }

  if (typeof window !== `undefined`) {
    useScrollPosition(({ prevPos, currPos }) => {
      if (!_scroller.current) return
      const dir = currPos.y > prevPos.y ? -1 : 1
      const yPos =
        _scroller.current.container.current.getBoundingClientRect().top
      const dif = window.innerHeight - yPos
      if (dif > 1) {
        _scroller.current.container.current.scrollLeft =
          _scroller.current.container.current.scrollLeft + 3 * dir
      }
    })
  }

  const step = () => {
    _scroller.current.container.current.scrollLeft =
      _scroller.current.container.current.scrollLeft + isScrolling * 10

    if (isScrolling !== null) requestRef.current = requestAnimationFrame(step)
  }

  useEffect(() => {
    if (isScrolling !== null) requestRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(requestRef.current)
  }, [isScrolling])

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const renderMediaItem = (mediaItem, i) => {
    return <ProductCardV2 key={i} idx={i} product={mediaItem} />
  }

  if (sort_by === "new_arrivals") {
    return (
      <div className={`${s.GalleryScroll} ${className && className}`}>
        <div className={s.GalleryScroll__header}>
          <div className={s.GalleryScroll__header___title}>
            {data.sortBy === "new_arrivals" && <h4>New Arrivals</h4>}
            <div className={s.GalleryScroll__header___titleDrag}>
              <span>Drag to explore</span>
              <div
                className={s.GalleryScroll__header___left}
                onMouseOver={() => setIsScrolling(-1)}
                onMouseOut={() => setIsScrolling(null)}
              >
                <Arrow />
              </div>
              <div
                className={s.GalleryScroll__header___right}
                onMouseOver={() => setIsScrolling(1)}
                onMouseOut={() => setIsScrolling(null)}
              >
                <Arrow />
              </div>
            </div>
          </div>
        </div>

        <div className={s.GalleryScroll__inner}>
          <ScrollContainer
            className={s.GalleryScroll__container}
            hideScrollbars={true}
            vertical={false}
            ref={_scroller}
          >
            {shopItems.map((mediaItem, i) => {
              return renderMediaItem(mediaItem, i)
            })}
          </ScrollContainer>
        </div>
      </div>
    )
  }
}
